/**
 * Screen camera (audit "camera animation"): a per-screen move — Ken-Burns
 * pans, punch in / pull back, shake — running over the screen's dwell time,
 * stored at `frame.customData.screenCamera`. `cameraAt` is a pure function
 * of (camera, progress) so export is deterministic; playback drives the real
 * viewport from the same numbers, so element animations keep playing under
 * the camera.
 */
import { gsap } from "gsap";

import { sampleTrack } from "./tracks";

import type { ClipTracks, TrackChannel, TrackKeyframe } from "./tracks";

export type CameraMove =
  | "panLeft"
  | "panRight"
  | "panUp"
  | "panDown"
  | "punchIn"
  | "pullBack"
  | "shake"
  | "keyframed";

export type ScreenCamera = {
  move: CameraMove;
  /** normalized strength: pan travel / extra zoom, clamped 0.05..1
   * (unused by "keyframed" — the curves carry the amounts) */
  amount: number;
  /** "keyframed" only: camZoom/camPanX/camPanY keyframe curves */
  keyframes?: ClipTracks;
};

export const CAMERA_MOVES: readonly CameraMove[] = [
  "panLeft",
  "panRight",
  "panUp",
  "panDown",
  "punchIn",
  "pullBack",
  "shake",
  "keyframed",
];

/** The camera's three keyframable channels — TrackChannel-shaped so the
 * graph editor and pure track ops work on them unchanged. `apply` is unused
 * (cameraAt samples directly) but keeps the shape honest. */
export const CAMERA_TRACK_CHANNELS: readonly TrackChannel[] = [
  {
    key: "camZoom",
    label: "Zoom",
    min: 1,
    max: 4,
    step: 0.05,
    unit: "×",
    neutral: 1,
    color: "#d6b45a",
    apply: () => {},
  },
  {
    key: "camPanX",
    label: "Pan X",
    min: -0.5,
    max: 0.5,
    step: 0.01,
    unit: "",
    neutral: 0,
    color: "#5ab0d6",
    apply: () => {},
  },
  {
    key: "camPanY",
    label: "Pan Y",
    min: -0.5,
    max: 0.5,
    step: 0.01,
    unit: "",
    neutral: 0,
    color: "#6fbf73",
    apply: () => {},
  },
];

const CAMERA_CHANNEL_KEYS = new Set(CAMERA_TRACK_CHANNELS.map((c) => c.key));

/** Same structural rules as isValidTracks, against the camera channels. */
const isValidCameraKeyframes = (value: unknown): value is ClipTracks => {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  for (const [channel, kfs] of Object.entries(value)) {
    if (!CAMERA_CHANNEL_KEYS.has(channel) || !Array.isArray(kfs)) {
      return false;
    }
    let prevT = -Infinity;
    for (const kf of kfs as TrackKeyframe[]) {
      if (
        typeof kf !== "object" ||
        kf === null ||
        typeof kf.t !== "number" ||
        !Number.isFinite(kf.t) ||
        kf.t < 0 ||
        kf.t > 1 ||
        kf.t < prevT ||
        typeof kf.v !== "number" ||
        !Number.isFinite(kf.v) ||
        (kf.ease !== undefined && typeof kf.ease !== "string")
      ) {
        return false;
      }
      prevT = kf.t;
    }
  }
  return true;
};

export const CAMERA_MOVE_LABELS: Record<CameraMove, string> = {
  panLeft: "Pan ←",
  panRight: "Pan →",
  panUp: "Pan ↑",
  panDown: "Pan ↓",
  punchIn: "Zoom In",
  pullBack: "Zoom Out",
  shake: "Shake",
  keyframed: "Keyframed",
};

const MIN_AMOUNT = 0.05;
const MAX_AMOUNT = 1;

/** Validated camera config for a frame, or null when absent/malformed. */
export const getScreenCamera = (frame: {
  customData?: Record<string, unknown> | null;
}): ScreenCamera | null => {
  const raw = frame.customData?.screenCamera as
    | Record<string, unknown>
    | undefined;
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const move = raw.move as CameraMove;
  if (!CAMERA_MOVES.includes(move)) {
    return null;
  }
  if (typeof raw.amount !== "number" || !Number.isFinite(raw.amount)) {
    return null;
  }
  return {
    move,
    amount: Math.min(MAX_AMOUNT, Math.max(MIN_AMOUNT, raw.amount)),
    // malformed curves are dropped (static camera), never rejected wholesale
    ...(move === "keyframed" && isValidCameraKeyframes(raw.keyframes)
      ? { keyframes: raw.keyframes }
      : {}),
  };
};

/** customData patch storing (or clearing, with null) a frame's camera. */
export const withScreenCamera = (
  frame: { customData?: Record<string, unknown> | null },
  camera: ScreenCamera | null,
): Record<string, unknown> => {
  const { screenCamera: _old, ...rest } = frame.customData ?? {};
  if (!camera) {
    return rest;
  }
  return { ...rest, screenCamera: camera };
};

const PAN_EASE = gsap.parseEase("power1.inOut");
const ZOOM_EASE = gsap.parseEase("power2.inOut");

const SHAKE_ZOOM_FRACTION = 0.2; // overscan so edge jitter never shows
const SHAKE_MAGNITUDE = 0.06; // frame-size fraction at amount=1
const SHAKE_TICKS = 40; // shake steps across the screen's dwell

/** Deterministic pseudo-random in [0, 1) from two integers. */
const prand = (a: number, b: number) => {
  let h = (a * 374761393 + b * 668265263) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
};

/** Camera state at normalized progress: dx/dy are view-center offsets in
 * frame-size fractions (positive = view moves right/down), zoom ≥ 1. */
export const cameraAt = (
  camera: ScreenCamera,
  progress: number,
): { dx: number; dy: number; zoom: number } => {
  const p = Math.min(1, Math.max(0, progress));
  const a = camera.amount;
  switch (camera.move) {
    case "keyframed": {
      const kfs = camera.keyframes;
      const sample = (key: string, neutral: number) =>
        kfs?.[key]?.length ? sampleTrack(kfs[key], p) : neutral;
      return {
        dx: sample("camPanX", 0),
        dy: sample("camPanY", 0),
        zoom: Math.max(1, sample("camZoom", 1)),
      };
    }
    case "panLeft":
    case "panRight":
    case "panUp":
    case "panDown": {
      // Ken Burns: constant zoom 1+a keeps frame edges out of view while
      // the center travels −a/2 → +a/2 along the axis
      const travel = (PAN_EASE(p) - 0.5) * a;
      const sign =
        camera.move === "panLeft" || camera.move === "panUp" ? -1 : 1;
      const horizontal =
        camera.move === "panLeft" || camera.move === "panRight";
      return {
        dx: horizontal ? sign * travel : 0,
        dy: horizontal ? 0 : sign * travel,
        zoom: 1 + a,
      };
    }
    case "punchIn":
      return { dx: 0, dy: 0, zoom: 1 + a * ZOOM_EASE(p) };
    case "pullBack":
      return { dx: 0, dy: 0, zoom: 1 + a * (1 - ZOOM_EASE(p)) };
    case "shake": {
      const tick = Math.floor(p * SHAKE_TICKS);
      return {
        dx: (prand(tick, 1) - 0.5) * a * SHAKE_MAGNITUDE,
        dy: (prand(tick, 2) - 0.5) * a * SHAKE_MAGNITUDE,
        zoom: 1 + a * SHAKE_ZOOM_FRACTION,
      };
    }
    default:
      return { dx: 0, dy: 0, zoom: 1 };
  }
};

/** Max zoom over the whole move — the export renders the screen canvas this
 * much sharper so punch-ins never pixelate. */
export const cameraOverscan = (camera: ScreenCamera): number => {
  if (camera.move === "keyframed") {
    const zooms = camera.keyframes?.camZoom ?? [];
    return Math.max(1, ...zooms.map((kf) => kf.v));
  }
  return camera.move === "shake"
    ? 1 + camera.amount * SHAKE_ZOOM_FRACTION
    : 1 + camera.amount;
};

/** Draws a screen canvas through the camera: dest rect = base size × zoom,
 * centered on the output center, shifted opposite the view offset. */
export const drawCameraFrame = (
  ctx: CanvasRenderingContext2D,
  canvas: CanvasImageSource,
  t: { dx: number; dy: number; zoom: number },
  dims: { width: number; height: number; baseW: number; baseH: number },
): void => {
  const destW = dims.baseW * t.zoom;
  const destH = dims.baseH * t.zoom;
  const cx = dims.width / 2 - t.dx * dims.baseW * t.zoom;
  const cy = dims.height / 2 - t.dy * dims.baseH * t.zoom;
  ctx.drawImage(canvas, cx - destW / 2, cy - destH / 2, destW, destH);
};
